import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { Store, Sparkles, Plus, Power, Palette } from "lucide-react";

export default function AdminStores() {
  const [stores, setStores] = useState(null);
  const [niche, setNiche] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => adminClient().get("/admin/stores").then(({ data }) => setStores(data.stores)).catch(() => setStores([]));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!niche.trim()) { toast.error("Enter a niche (e.g. pet lovers)."); return; }
    setBusy(true);
    try {
      const { data } = await adminClient().post("/admin/stores/generate", { niche: niche.trim() });
      toast.success(`Created store: ${data.name}`);
      setNiche(""); load();
    } catch { toast.error("Could not generate store."); } finally { setBusy(false); }
  };

  const toggle = async (st) => {
    try {
      if (st.active) { await adminClient().delete(`/admin/stores/${st.slug}`); }
      else { await adminClient().put(`/admin/stores/${st.slug}`, { active: true }); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update store."); }
  };

  if (!stores) return <p className="text-sm text-muted-foreground">Loading stores…</p>;

  return (
    <div data-testid="admin-stores">
      <div className="flex items-center gap-2 mb-1"><Store className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Store Creator</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Multiple branded storefronts running on <strong>one shared product database & automation engine</strong>. Generate a new themed store for any niche in one click.</p>

      <div className="border border-black/10 bg-taven-gray/40 p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="store-generator">
        <label className="text-sm flex-1 min-w-[240px]">Generate a new store for a niche
          <input value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="e.g. pet lovers, home office, gym"
            className="block w-full mt-1 border border-black/15 px-3 py-2 text-sm outline-none focus:border-taven-gold" data-testid="store-niche-input" />
        </label>
        <button onClick={generate} disabled={busy} className="flex items-center gap-1.5 bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50" data-testid="store-generate-btn">
          <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Generate Store"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stores-grid">
        {stores.map((st) => (
          <div key={st.slug} className={`border p-4 ${st.active ? "border-black/10 bg-white" : "border-black/10 bg-black/5 opacity-70"}`} data-testid={`store-card-${st.slug}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full border border-black/10" style={{ background: st.branding?.primary_color }} />
              <span className="w-6 h-6 rounded-full border border-black/10" style={{ background: st.branding?.accent_color }} />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-auto">{st.default ? "Flagship" : st.ai_generated ? "Generated" : "Preset"}</span>
            </div>
            <h3 className="font-black font-display text-lg">{st.name}</h3>
            <p className="text-xs text-muted-foreground mb-2">{st.tagline}</p>
            <p className="text-[11px] text-muted-foreground mb-1"><strong>Hero:</strong> {st.hero?.headline}</p>
            <p className="text-[11px] text-muted-foreground mb-2"><Palette className="w-3 h-3 inline mr-1" />{(st.featured_collections || []).slice(0, 4).join(", ")}</p>
            <div className="flex items-center justify-between">
              <span className={`text-[10px] font-bold uppercase tracking-wider ${st.active ? "text-green-700" : "text-muted-foreground"}`}>{st.active ? "Active" : "Inactive"}</span>
              {!st.default && (
                <button onClick={() => toggle(st)} className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider border border-black/15 px-2.5 py-1 hover:bg-taven-gray" data-testid={`store-toggle-${st.slug}`}>
                  <Power className="w-3 h-3" /> {st.active ? "Disable" : "Enable"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
